// §7.7 — **Export everything**. Non-negotiable: it exists in both modes.
//
// A signed-in user gets `GET /api/export` (M12). A guest gets this: the OPFS tree zipped in a
// worker, with a main-thread `zipSync` fallback for a browser that will not give us one. Either
// way the answer to "can I get my files out of this thing" is yes, without an account.
import { ROOT_APPS, ROOT_USERS, joinVfs, type VfsPath } from '@hangar/shared';
import { zipSync, type Zippable } from 'fflate';
import type { OpfsStore } from './opfs';
import type { ExportMessage, ExportRequest } from './export-worker';

/**
 * What "everything" means. `/System` is left out deliberately: its wallpapers and sounds are
 * generated from code (D-043) and its `.versions` tree is kernel bookkeeping, not the user's
 * documents. Both would make the zip bigger than the data it is supposed to rescue.
 */
export const EXPORT_ROOTS: readonly VfsPath[] = [ROOT_USERS, ROOT_APPS];

export interface ExportResult {
  blob: Blob;
  files: number;
  /** Uncompressed total, so the UI can say what was exported rather than what it compressed to. */
  bytes: number;
}

export interface ExportService {
  exportEverything(): Promise<ExportResult>;
  /** `hangar-export-YYYY-MM-DD.zip`. */
  suggestedFilename(): string;
}

export interface ExportOptions {
  store: OpfsStore;
  roots?: readonly VfsPath[];
  /** Set false to force the main-thread path — the fallback needs proving too. */
  useWorker?: boolean;
  now?: () => number;
}

export function createExportService(options: ExportOptions): ExportService {
  const roots = options.roots ?? EXPORT_ROOTS;
  const now = options.now ?? (() => Date.now());
  let requestId = 0;

  async function viaWorker(): Promise<ExportResult | null> {
    if (options.useWorker === false || typeof Worker === 'undefined') return null;
    let worker: Worker;
    try {
      worker = new Worker(new URL('./export-worker.ts', import.meta.url), { type: 'module' });
    } catch {
      return null;
    }

    const id = (requestId += 1);
    try {
      return await new Promise<ExportResult | null>((resolve, reject) => {
        worker.addEventListener('message', (event: MessageEvent<ExportMessage>) => {
          const message = event.data;
          if (message.id !== id) return;
          if (message.kind === 'export:fail') {
            reject(new Error(message.message));
            return;
          }
          resolve({
            blob: new Blob([message.zip], { type: 'application/zip' }),
            files: message.files,
            bytes: message.bytes,
          });
        });
        // A worker that cannot even load is a fallback, not a failure.
        worker.addEventListener('error', () => resolve(null));
        const request: ExportRequest = { kind: 'export', id, roots: [...roots] };
        worker.postMessage(request);
      });
    } finally {
      worker.terminate();
    }
  }

  /** The same walk, on the main thread, through the store rather than raw handles. */
  async function onMainThread(): Promise<ExportResult> {
    const zippable: Zippable = {};
    let files = 0;
    let bytes = 0;

    const walk = async (dir: VfsPath): Promise<number> => {
      let seen = 0;
      let entries;
      try {
        entries = await options.store.list(dir);
      } catch {
        return 0; // a root that does not exist yet
      }
      for (const entry of entries) {
        const relative = entry.path.slice(1);
        if (entry.isDir) {
          const nested = await walk(entry.path);
          if (nested === 0) zippable[`${relative}/`] = new Uint8Array();
          seen += nested;
          continue;
        }
        zippable[relative] = await options.store.read(entry.path);
        files += 1;
        bytes += entry.size;
        seen += 1;
      }
      return seen;
    };

    for (const root of roots) {
      const found = await walk(root);
      if (found === 0) zippable[`${root.slice(1)}/`] = new Uint8Array();
    }

    const zipped = zipSync(zippable, { level: 6 });
    return { blob: new Blob([zipped], { type: 'application/zip' }), files, bytes };
  }

  return {
    async exportEverything() {
      return (await viaWorker()) ?? onMainThread();
    },

    suggestedFilename() {
      const stamp = new Date(now()).toISOString().slice(0, 10);
      return `hangar-export-${stamp}.zip`;
    },
  };
}

/** Exported for the tests: the zip entry name a VFS path maps to. */
export function zipEntryName(path: VfsPath): string {
  return path.slice(1);
}

/** Exported for the tests: the VFS path a zip entry name came from. */
export function vfsPathFor(entryName: string): VfsPath {
  return joinVfs('/', entryName);
}
